import {
  computed,
  inject,
  Injector,
  runInInjectionContext,
  Signal,
  Type,
} from '@angular/core';
import {
  buttonElement,
  checkboxElement,
  checkboxGroupElement,
  customElement,
  dateElement,
  datetimeElement,
  dividerElement,
  dropdownCountryElement,
  dropdownElement,
  dropdownPhoneElement,
  durationElement,
  DvFormElementConfig,
  DvFormElementType,
  groupElement,
  infoElement,
  lineBreakElement,
  numberElement,
  passwordElement,
  radioElement,
  searchElement,
  spaceElement,
  splitButtonElement,
  textareaElement,
  textElement,
  timeElement,
  toggleElement,
} from '@devinco/ui/signal-forms';
import { ColSpan } from '@devinco/ui/dds';
import { DvDropdownDatasourceService } from '@shared/dv-form/services/dv-dropdown-datasource.service';
import {
  SignalFormElementConfig,
  VtSignalFormElementExtra,
} from '../interfaces/signal-form-element.config';
import { translateConfig } from './translate.utils';
import { DropdownContext } from '@shared/dv-form/interfaces/dropdown-context';

type VtSignalFormElementInput<
  TData,
  K extends DvFormElementType,
  TComponent = any,
> = Omit<DvFormElementConfig<TData, K, TComponent>, 'type'> & {
  extra?: VtSignalFormElementExtra;
};

type Factory<K extends DvFormElementType> = (
  config: Omit<DvFormElementConfig<any, K>, 'type'>
) => DvFormElementConfig<any, K>;

type OptionalFactory<K extends DvFormElementType> = (
  config?: Partial<Omit<DvFormElementConfig<any, K>, 'type'>>
) => DvFormElementConfig<any, K>;

function wrap<TData, K extends DvFormElementType>(
  factory: Factory<K>,
  config: VtSignalFormElementInput<TData, K>
): SignalFormElementConfig<TData> {
  const translated = translateConfig(config as any);
  return {
    ...(factory(translated) as DvFormElementConfig<TData, K>),
    extra: config.extra,
  } as SignalFormElementConfig<TData>;
}

function wrapOptional<TData, K extends DvFormElementType>(
  factory: OptionalFactory<K>,
  config?: Partial<VtSignalFormElementInput<TData, K>>
): SignalFormElementConfig<TData> {
  const translated = config ? translateConfig(config as any) : undefined;
  return {
    ...(factory(translated as any) as DvFormElementConfig<TData, K>),
    extra: config?.extra,
  } as SignalFormElementConfig<TData>;
}

export const text = <T>(
  c: VtSignalFormElementInput<T, 'text'>
): SignalFormElementConfig<T> => wrap(textElement, c);
export const textarea = <T>(
  c: VtSignalFormElementInput<T, 'textarea'>
): SignalFormElementConfig<T> => wrap(textareaElement, c);
export const date = <T>(
  c: VtSignalFormElementInput<T, 'date'>
): SignalFormElementConfig<T> => wrap(dateElement, c);
export const datetime = <T>(
  c: VtSignalFormElementInput<T, 'datetime'>
): SignalFormElementConfig<T> => wrap(datetimeElement, c);
export const time = <T>(
  c: VtSignalFormElementInput<T, 'time'>
): SignalFormElementConfig<T> => wrap(timeElement, c);
export const checkbox = <T>(
  c: VtSignalFormElementInput<T, 'checkbox'>
): SignalFormElementConfig<T> => wrap(checkboxElement, c);
export const checkboxGroup = <T>(
  c: VtSignalFormElementInput<T, 'checkbox-group'>
): SignalFormElementConfig<T> => wrap(checkboxGroupElement, c);
export const numberInput = <T>(
  c: VtSignalFormElementInput<T, 'number'>
): SignalFormElementConfig<T> => wrap(numberElement, c);
export const password = <T>(
  c: VtSignalFormElementInput<T, 'password'>
): SignalFormElementConfig<T> => wrap(passwordElement, c);
export const radio = <T>(
  c: VtSignalFormElementInput<T, 'radio'>
): SignalFormElementConfig<T> => wrap(radioElement, c);
export const search = <T>(
  c: VtSignalFormElementInput<T, 'search'>
): SignalFormElementConfig<T> => wrap(searchElement, c);
export const toggle = <T>(
  c: VtSignalFormElementInput<T, 'toggle'>
): SignalFormElementConfig<T> => wrap(toggleElement, c);
export const duration = <T>(
  c: VtSignalFormElementInput<T, 'duration'>
): SignalFormElementConfig<T> => wrap(durationElement, c);
export const info = <T>(
  c: VtSignalFormElementInput<T, 'info'>
): SignalFormElementConfig<T> => wrap(infoElement, c);
export const button = <T>(
  c: VtSignalFormElementInput<T, 'button'>
): SignalFormElementConfig<T> => wrap(buttonElement, c);
export const splitButton = <T>(
  c: VtSignalFormElementInput<T, 'split-button'>
): SignalFormElementConfig<T> => wrap(splitButtonElement, c);
export const elementGroup = <T>(
  c: VtSignalFormElementInput<T, 'element-group'>
): SignalFormElementConfig<T> => wrap(groupElement, c);
export const countryDropdown = <T>(
  c: VtSignalFormElementInput<T, 'dropdown-country'>
): SignalFormElementConfig<T> => {
  c.props ??= {};
  (c.props as any).flagsPath ??= 'assets/flags/';
  return wrap(dropdownCountryElement, c);
};
export const phoneDropdown = <T>(
  c: VtSignalFormElementInput<T, 'dropdown-phone'>
): SignalFormElementConfig<T> => {
  c.props ??= {};
  (c.props as any).flagsPath ??= 'assets/flags/';
  return wrap(dropdownPhoneElement, c);
};
export const divider = <T>(
  c?: Partial<VtSignalFormElementInput<T, 'divider'>>
): SignalFormElementConfig<T> => wrapOptional(dividerElement, c);
export const space = <T>(
  c?: Partial<VtSignalFormElementInput<T, 'space'>>
): SignalFormElementConfig<T> => wrapOptional(spaceElement, c);
export const lineBreak = <T>(
  c?: Partial<VtSignalFormElementInput<T, 'line-break'>>
): SignalFormElementConfig<T> => wrapOptional(lineBreakElement, c);

export function dropdown<T>(
  c: VtSignalFormElementInput<T, 'dropdown'>
): SignalFormElementConfig<T> {
  c.props ??= {};
  c.extra ??= {};
  if (!c.props.items?.length) {
    c.props.datasource ??= {};
    c.props.datasource.chunkSize ??= 500;
    c.props.datasource.loader ??= inject(DvDropdownDatasourceService).loader;
  }

  if (c.extra.translateLabels?.length && c.props.context) {
    (c.props.context as DropdownContext<T>).translateLabels ??=
      c.extra.translateLabels;
  }

  return wrap(dropdownElement, c);
}

export function custom<T, TComponent = any>(c: {
  path?: VtSignalFormElementInput<T, 'custom'>['path'];
  label?: string;
  colspan?: ColSpan;
  props?: {
    component?: Type<TComponent>;
    inputs?: any;
    outputs?: any;
  };
  extra?: VtSignalFormElementExtra;
}): SignalFormElementConfig<T> {
  const translated = translateConfig(c as any);
  return {
    ...(customElement<T, TComponent>(translated as any) as DvFormElementConfig<
      T,
      'custom'
    >),
    extra: c.extra,
  } as SignalFormElementConfig<T>;
}

/**
 * Like `computed()`, but safe to use for building arrays of signal-form
 * elements via factory functions (e.g. `f.dropdown()`) that call `inject()`
 * internally. Captures the current injection context eagerly (must be called
 * from a constructor, field initializer, or another injection context) and
 * restores it on every recomputation, so deferred factory calls don't throw NG0203.
 */
export function computedFormElements<T>(factory: () => T): Signal<T> {
  const injector = inject(Injector);
  return computed(() => runInInjectionContext(injector, factory));
}
